import { ClientDiscount, ClientDiscountGroup } from "utils/interfaces"
import { CURRENCY } from "utils/constants"
import { INITIATOR } from "modules/discount/constants"
import { formatPrice, formatSessionNumber, monthsNameInDativeCase } from "utils/utils"
import { numberOfUse, sessionNounDeclensionByNumeral } from "utils/infrastructure/wording-utils"

export class DiscountFormatter {
    constructor(private discount: ClientDiscount) {}

    getName() {
        if (
            this.discount.group === ClientDiscountGroup.abonements &&
            this.discount.reminder_deposit
        ) {
            return `Ваш Абонемент: ${formatPrice(this.discount.reminder_deposit, CURRENCY.RUB)}`
        }

        const arr = []

        if (this.discount.percent > 0) {
            if (this.discount.group === ClientDiscountGroup.referralProgram) {
                if (this.discount.percent * 100 === 10) {
                    arr.push(`Скидка за рекомендацию ${this.discount.percent * 100}%`)
                } else if (this.discount.percent * 100 === 15) {
                    arr.push(`Скидка от друга ${this.discount.percent * 100}%`)
                }
            } else {
                arr.push(`Скидка ${this.discount.percent * 100}%`)
            }
        }

        if (this.discount.flat > 0 && !this.discount.deposit_mode) {
            arr.push(`Скидка ${formatPrice(this.discount.flat, CURRENCY.RUB)}`)
        }

        if (this.discount.deposit_mode && this.discount.reminder_deposit) {
            arr.push(`Ваш депозит ${formatPrice(this.discount.reminder_deposit, CURRENCY.RUB)}`)
        }

        return arr.join(". ")
    }

    getDescription(initiator?: INITIATOR) {
        if (this.discount.disabled_by_balance) {
            return "Скидка для вашей компании временно неактивна."
        }

        if (
            this.discount.group === ClientDiscountGroup.referralProgram &&
            this.discount.percent * 100 === 10 &&
            this.discount.reminder_usages
        ) {
            return `На ${this.discount.reminder_usages} ${sessionNounDeclensionByNumeral(
                this.discount.reminder_usages
            )}. Примените скидку при записи к психологу.`
        }

        const usagesReminder = this.formatUsagesReminder()
        const monthlyUsagesReminder = this.formatMonthlyUsagesReminder(initiator)
        const depositReminder = this.formatDepositReminder()
        const sessionLimit = this.formatSessionLimit()
        const promoCode = this.formatPromoCode()
        const dateLimit = this.formatDateLimit()

        return [
            usagesReminder,
            monthlyUsagesReminder,
            depositReminder,
            sessionLimit,
            promoCode,
            dateLimit,
        ]
            .filter((s) => s)
            .join(" ")
    }

    private formatUsagesReminder() {
        if (!this.discount.reminder_usages) return
        return `Осталось ${
            this.discount.reminder_usages
                ? `${this.discount.reminder_usages} ${numberOfUse(this.discount.reminder_usages)}.`
                : `неограниченное количество ${numberOfUse(0)}.`
        }`
    }

    private formatMonthlyUsagesReminder(initiator?: INITIATOR) {
        if (this.discount.monthly_reminder_usages === null) return
        if (!!initiator && initiator === INITIATOR.CLIENT_ACCOUNT)
            return `Максимальное количество сессий в месяц по скидке ${this.discount.monthly_reminder_usages}.`

        const monthNoun = monthsNameInDativeCase(this.discount.month - 1)
        return `Доступно сессий в ${monthNoun} ${this.discount.monthly_reminder_usages}.`
    }

    private formatDepositReminder() {
        if (!this.discount.deposit_mode) return

        if (this.discount.group === ClientDiscountGroup.abonements) {
            return "Скидка на сессии в пределах абонемента."
        }

        return `Скидка на сессии в пределах депозита.`
    }

    private formatSessionLimit() {
        if (
            this.discount.session_number_limit_from === null &&
            this.discount.session_number_limit_to === null
        )
            return
        const arr = []
        arr.push(`Применяется`)
        if (
            this.discount.session_number_limit_from !== null &&
            this.discount.session_number_limit_to !== null &&
            this.discount.session_number_limit_from === this.discount.session_number_limit_to - 1
        ) {
            arr.push(`на ${formatSessionNumber(this.discount.session_number_limit_from)}`)
        } else {
            if (this.discount.session_number_limit_from !== null) {
                arr.push(`с ${this.discount.session_number_limit_from}`)
            }

            if (this.discount.session_number_limit_to !== null) {
                arr.push(`по ${this.discount.session_number_limit_to - 1}`)
            }
        }

        arr.push("сессию.")
        return arr.join(" ")
    }

    formatPromoCode() {
        if (!this.discount.promo_code) return
        return `Промокод ${this.discount.promo_code}.`
    }

    formatDateLimit() {
        if (!this.discount.end_day) return
        return `Скидка действительна до ${this.discount.end_day}.`
    }
}
